import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import useProduction from '@/src/Production/components/hooks/useProduction';
import { fullName } from './shared';

/* Design — status-grouped request list with deliverable progress. */

const STATUS_ORDER = [
    'needs_artwork',
    'up_next',
    'to_do',
    'doing',
    'waiting_on_files',
    'client_designing',
    'pending_internal_approval',
    'sent_to_client',
    'client_changes',
    'ready_for_production',
    'archived',
];

export default function DesignV2() {
    const { getDesignRequests } = useProduction();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            const data = await getDesignRequests().catch(() => []);
            if (!mounted) return;
            setRows(data ?? []);
            setLoading(false);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const groups = STATUS_ORDER.map((status) => ({
        status,
        rows: rows.filter((r) => r.status === status),
    })).filter((g) => g.rows.length > 0);

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-3.5">Jobs · Design</div>

            {loading && (
                <div className="py-10 text-[13px] text-faint">Loading…</div>
            )}
            {!loading && rows.length === 0 && (
                <div className="py-10 text-[13px] text-faint">
                    No design requests.
                </div>
            )}
            {groups.map((g) => (
                <section key={g.status} className="mb-1">
                    <div className="ps-label mb-1.5 mt-6 first:mt-0">
                        {g.status.replaceAll('_', ' ')} · {g.rows.length}
                    </div>
                    {g.rows.map((r) => (
                        <div
                            key={r.id}
                            className="flex items-center gap-3.5 border-t border-line px-0.5 py-[10px] text-[12.5px] first:border-t-0"
                        >
                            <span className="min-w-0 flex-1 truncate font-semibold">
                                {r.activationName}
                            </span>
                            <span className="w-[150px] flex-none truncate text-muted-foreground">
                                {r.designerFirstName
                                    ? fullName({
                                          firstName: r.designerFirstName,
                                          lastName: r.designerLastName,
                                      })
                                    : 'Unassigned'}
                            </span>
                            <span className="w-[110px] flex-none text-muted-foreground">
                                {r.dueDate
                                    ? `due ${moment(r.dueDate).format('MMM D')}`
                                    : 'no due date'}
                            </span>
                            <span className="w-[110px] flex-none tabular-nums text-muted-foreground">
                                {r.deliveredCount ?? 0}/{r.deliverableCount ?? 0}{' '}
                                delivered
                            </span>
                            {r.clientApprovedAt && (
                                <span className="ps-label flex-none normal-case text-good">
                                    Approved
                                </span>
                            )}
                        </div>
                    ))}
                </section>
            ))}
        </main>
    );
}
